
import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { BackButton } from '@/components/ui/back-button';
import { ArrowUpIcon } from '@heroicons/react/24/solid';
import { HeroBackground } from '@/components/HeroBackground';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { TypewriterText } from '@/components/agent-wizard/TypewriterText';
import { Phase1BusinessAnalysis } from '@/components/agent-wizard/Phase1BusinessAnalysis';
import { Phase2CampaignSetup } from '@/components/agent-wizard/Phase2CampaignSetup';
import { Phase3Review } from '@/components/agent-wizard/Phase3Review';
import { useSidebar } from '@/layouts/DashboardLayout';
import {
  agentWizardService,
  type Phase1Proposal,
  type Phase2Proposal,
} from '@/lib/services/agent-wizard';

type WizardState =
  | { step: 'url-input' }
  | { step: 'manual-input' }
  | { step: 'loading' }
  | { step: 'scrape-failed'; sessionId: string }
  | { step: 'phase'; sessionId: string };

export default function AgentWizard() {
  const navigate = useNavigate();
  const { isSidebarOpen, setSidebarOpen } = useSidebar();
  const sidebarWasOpen = useRef(isSidebarOpen);

  useEffect(() => {
    sidebarWasOpen.current = isSidebarOpen;
    setSidebarOpen(false);
    return () => {
      setSidebarOpen(sidebarWasOpen.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [state, setState] = useState<WizardState>({ step: 'url-input' });
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  const [currentPhase, setCurrentPhase] = useState(1);
  const [phaseData, setPhaseData] = useState<Record<string, unknown>>({});
  const [challenges, setChallenges] = useState<string[]>([]);

  const [phase1Proposal, setPhase1Proposal] = useState<Phase1Proposal | null>(null);
  const [phase2Proposal, setPhase2Proposal] = useState<Phase2Proposal | null>(null);

  const [messages, setMessages] = useState<{ role: string; content: string; createdAt?: string }[]>([]);
  const [isDiscussing, setIsDiscussing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [tokenUsage, setTokenUsage] = useState({ inputTokens: 0, outputTokens: 0 });
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [activityFeed, setActivityFeed] = useState<string[]>([]);
  const [chatInput, setChatInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Transition animation states
  const [showDataPanels, setShowDataPanels] = useState(false);
  const [showBackButton, setShowBackButton] = useState(false);
  const [showChatMessages, setShowChatMessages] = useState(false);
  const [showChatInput, setShowChatInput] = useState(false);
  const showChat = showChatMessages; // keep existing references working
  const [_panelTransitioning, setPanelTransitioning] = useState(false);
  const [phaseLoading, setPhaseLoading] = useState(false);

  const addUsage = (usage?: { inputTokens: number; outputTokens: number }) => {
    if (usage) {
      setTokenUsage(prev => ({
        inputTokens: prev.inputTokens + usage.inputTokens,
        outputTokens: prev.outputTokens + usage.outputTokens,
      }));
    }
  };

  // Ensure proposal has all required fields with defaults
  const normalizePhase1 = (p: any): Phase1Proposal => ({
    businessSummary: p?.businessSummary || '',
    offerings: Array.isArray(p?.offerings) ? p.offerings : [],
    icp: {
      title: p?.icp?.title || '',
      seniority: p?.icp?.seniority || '',
      industry: p?.icp?.industry || '',
      companySize: p?.icp?.companySize || '',
      geography: p?.icp?.geography || '',
      customFilters: Array.isArray(p?.icp?.customFilters) ? p.icp.customFilters : [],
    },
    reasoning: p?.reasoning || '',
  });

  const normalizePhase2 = (p: any): Phase2Proposal => ({
    outreachGoal: p?.outreachGoal || '',
    messagingTone: p?.messagingTone || 'professional',
    sequenceSteps: Array.isArray(p?.sequenceSteps) ? p.sequenceSteps : [],
    reasoning: p?.reasoning || '',
  });

  const handleExit = () => {
    if (state.step === 'url-input' || state.step === 'manual-input') {
      navigate('/dashboard/campaigns');
    } else {
      setShowExitDialog(true);
    }
  };

  // Chat
  const allMessages = [...messages];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // handleChatSubmit defined after handleSendMessage below

  const handleStartWithUrl = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!websiteUrl.trim()) return;

    setState({ step: 'loading' });
    setActivityFeed([]);

    // Show progress steps while API works
    const domain = websiteUrl.trim().replace('https://', '').replace('http://', '').replace('www.', '').split('/')[0];
    const progressSteps = [
      { text: `Visiting ${domain}`, delay: 1500 },
      { text: 'Reading the homepage', delay: 2800 },
      { text: 'Looking for key pages', delay: 2200 },
      { text: 'Reading about the company', delay: 3000 },
      { text: 'Reviewing services offered', delay: 2600 },
      { text: 'Understanding value proposition', delay: 3400 },
      { text: 'Identifying target market', delay: 2800 },
      { text: 'Building prospect profile', delay: 3200 },
      { text: 'Putting it all together', delay: 2400 },
    ];
    let cancelled = false;
    const runProgress = async () => {
      for (const step of progressSteps) {
        if (cancelled) break;
        const jitter = step.delay + (Math.random() - 0.5) * 800;
        await new Promise(r => setTimeout(r, jitter));
        if (cancelled) break;
        setActivityFeed(prev => [...prev, step.text]);
      }
    };
    runProgress();

    try {
      const result = await agentWizardService.start({ websiteUrl: websiteUrl.trim() });
      cancelled = true;
      addUsage(result.usage);
      setPhase1Proposal(normalizePhase1(result.proposal));
      setCurrentPhase(1);

      // Fire intro generation immediately — runs in parallel with transitions
      const introPromise = agentWizardService.sendIntro(result.sessionId);

      // Brief pause before transitioning
      await new Promise(r => setTimeout(r, 800));

      // Clear activity and transition to phase view
      setActivityFeed([]);
      setPanelTransitioning(true);
      setState({ step: 'phase', sessionId: result.sessionId });
      await new Promise(r => setTimeout(r, 600));
      setShowDataPanels(true);
      await new Promise(r => setTimeout(r, 400));
      setShowBackButton(true);
      await new Promise(r => setTimeout(r, 300));
      setShowChatMessages(true);
      setIsDiscussing(true);
      await new Promise(r => setTimeout(r, 400));
      setShowChatInput(true);
      setPanelTransitioning(false);

      // Wait for intro (already been running during transitions)
      try {
        const introResult = await introPromise;
        addUsage(introResult.usage);
        setIsDiscussing(false);
        setMessages([{ role: 'assistant', content: introResult.reply }]);
      } catch {
        setIsDiscussing(false);
        setMessages([{ role: 'assistant', content: 'I\'ve put together a profile based on your website. Take a look on the right and let me know if anything needs changing.' }]);
      }
    } catch {
      toast.error('Failed to analyse website. Please try again.');
      cancelled = true;
      setActivityFeed([]);
      setState({ step: 'url-input' });
    }
  };

  const handleStartWithDescription = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualDescription.trim()) return;

    if (state.step === 'scrape-failed') {
      const sessionId = state.sessionId;
      setState({ step: 'loading' });

      try {
        const result = await agentWizardService.submitManualDescription(sessionId, manualDescription.trim());
        setPhase1Proposal(normalizePhase1(result.proposal));
        setCurrentPhase(1);
        setState({ step: 'phase', sessionId });
      } catch {
        toast.error('Failed to analyse business description');
        setState({ step: 'scrape-failed', sessionId });
      }
    } else {
      setState({ step: 'loading' });

      try {
        const result = await agentWizardService.start({ manualDescription: manualDescription.trim() });
        addUsage(result.usage);
        setPhase1Proposal(normalizePhase1(result.proposal));
        setCurrentPhase(1);
        setState({ step: 'phase', sessionId: result.sessionId });
      } catch {
        toast.error('Failed to analyse business description');
        setState({ step: 'manual-input' });
      }
    }
  };

  const handleSendMessage = useCallback(async (message: string) => {
    if (state.step !== 'phase') return;

    setIsDiscussing(true);
    setMessages(prev => [...prev, { role: 'user', content: message }]);

    try {
      const currentData = currentPhase === 1 ? phase1Proposal : phase2Proposal;
      const result = await agentWizardService.sendMessage(
        state.sessionId,
        message,
        currentData as unknown as Record<string, unknown>
      );
      addUsage(result.usage);

      if (result.proposal) {
        if (currentPhase === 1) {
          setPhase1Proposal(normalizePhase1(result.proposal));
        } else if (currentPhase === 2) {
          setPhase2Proposal(normalizePhase2(result.proposal));
        }
        setMessages(prev => [...prev, { role: 'assistant', content: 'Proposal updated based on your feedback.' }]);
      } else {
        // Claude replied conversationally without updating the proposal
        setMessages(prev => [...prev, { role: 'assistant', content: result.reply || 'Let me know if you want me to change anything.' }]);
      }
    } catch {
      toast.error('Failed to process your message');
    } finally {
      setIsDiscussing(false);
    }
  }, [state, currentPhase, phase1Proposal, phase2Proposal]);

  const handleChatSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim() || isDiscussing) return;
    const msg = chatInput.trim();
    setChatInput('');
    await handleSendMessage(msg);
  }, [chatInput, isDiscussing, handleSendMessage]);

  const handleConfirm = useCallback(async () => {
    if (state.step !== 'phase') return;

    if (currentPhase === 3) {
      setIsConfirming(true);
      try {
        const result = await agentWizardService.complete(state.sessionId);
        toast.success('Campaign created!');
        navigate(`/dashboard/campaigns/${result.campaignId}/edit`);
      } catch {
        toast.error('Failed to create agent and campaign');
      } finally {
        setIsConfirming(false);
      }
      return;
    }

    const confirmedData = currentPhase === 1 ? phase1Proposal : phase2Proposal;
    if (!confirmedData) return;

    setIsConfirming(true);
    setChallenges([]);

    // Fade out data panels, show animated bg with activity
    setShowDataPanels(false);
    setPhaseLoading(true);
    setActivityFeed([]);
    await new Promise(r => setTimeout(r, 500));

    // Show activity steps while API runs
    const phaseActivities = currentPhase === 1
      ? ['Confirming business profile', 'Designing campaign approach', 'Crafting email sequence', 'Setting messaging tone']
      : ['Confirming campaign setup', 'Preparing review'];

    // Start activity feed in parallel with API call
    const activityPromise = (async () => {
      for (const activity of phaseActivities) {
        setActivityFeed(prev => [...prev, activity]);
        await new Promise(r => setTimeout(r, 800));
      }
    })();

    try {
      const [result] = await Promise.all([
        agentWizardService.confirmPhase(
          state.sessionId,
          confirmedData as unknown as Record<string, unknown>
        ),
        activityPromise,
      ]);

      if (!result.approved) {
        setChallenges(result.challenges || []);
        toast.error('The AI has some concerns. Please address them before continuing.');
        setActivityFeed([]);
        setPhaseLoading(false);
        setShowDataPanels(true);
        setIsConfirming(false);
        return;
      }

      // Show final activity step
      setActivityFeed(prev => [...prev, 'Done']);
      await new Promise(r => setTimeout(r, 600));

      addUsage(result.usage);
      setCurrentPhase(result.currentPhase);
      setPhaseData(result.phaseData || {
        ...(currentPhase === 1 ? { phase1: confirmedData } : {}),
        ...(currentPhase === 2 ? { phase1: phaseData.phase1, phase2: confirmedData } : {}),
      });
      setChallenges([]);

      if (result.proposal && result.currentPhase === 2) {
        setPhase2Proposal(normalizePhase2(result.proposal));
      }

      // Fade out animated bg, show new data panels
      setActivityFeed([]);
      setPhaseLoading(false);
      setIsConfirming(false);
      await new Promise(r => setTimeout(r, 300));
      setShowDataPanels(true);

      // Get AI to introduce the new phase
      setIsDiscussing(true);
      try {
        const phasePrompt = result.currentPhase === 2
          ? 'You just confirmed the business profile and generated a campaign setup (outreach goal, messaging tone, email sequence) on the right. Write a brief message (2-3 sentences) telling the user what you\'ve done and to review the campaign details. Own it. Be casual and direct.'
          : 'The user just confirmed their campaign setup. Everything is ready for final review on the right. Write a brief message (2-3 sentences) telling them to check it over and hit Create Agent & Campaign when ready. Be casual and direct.';
        const phaseIntro = await agentWizardService.sendIntro(state.sessionId, phasePrompt);
        addUsage(phaseIntro.usage);
        setIsDiscussing(false);
        setMessages(prev => [...prev, { role: 'assistant', content: phaseIntro.reply }]);
      } catch {
        setIsDiscussing(false);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: result.currentPhase === 2
            ? 'Campaign setup is on the right. Review and adjust anything, or ask me to change it.'
            : 'Everything\'s ready for review. Hit Create Agent & Campaign when you\'re good.'
        }]);
      }
    } catch {
      toast.error('Failed to confirm phase');
      setActivityFeed([]);
      setPhaseLoading(false);
      setShowDataPanels(true);
    } finally {
      setIsConfirming(false);
    }
  }, [state, currentPhase, phase1Proposal, phase2Proposal, phaseData, navigate]);

  const handleBack = useCallback(() => {
    if (currentPhase > 1) {
      setCurrentPhase(currentPhase - 1);
      setChallenges([]);
    }
  }, [currentPhase]);

  const exitDialog = (
    <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Leave agent setup?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Your progress hasn't been saved. You'll need to start again if you leave now.
        </p>
        <div className="flex justify-end gap-3 mt-4">
          <Button variant="secondary" onClick={() => setShowExitDialog(false)}>
            Continue setup
          </Button>
          <Button variant="destructive" onClick={() => navigate('/dashboard/campaigns')}>
            Leave
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  // Token counter debug overlay (fixed bottom-left across all screens)
  const tokenCounter = (
    <div className="fixed bottom-4 left-4 text-[10px] text-muted-foreground/50 font-mono z-50">
      tokens — in:{tokenUsage.inputTokens.toLocaleString()} out:{tokenUsage.outputTokens.toLocaleString()}
    </div>
  );


  const isLoading = state.step === 'loading';
  const isPhase = state.step === 'phase';
  const isSetup = state.step === 'url-input' || state.step === 'manual-input' || state.step === 'scrape-failed';

  return (<>
    {exitDialog}
    {tokenCounter}
    <div className="h-screen pl-4 pr-2 py-2">
      <div className="h-full bg-card rounded-panel flex overflow-hidden shadow-lg">
        {/* Left side */}
        <div className="flex-1 flex flex-col relative">
          {/* Back button */}
          {(isSetup || isPhase) && (
            <div className={`absolute top-4 left-4 z-10 ${isPhase ? `transition-[opacity] duration-500 ${showBackButton ? 'opacity-100' : 'opacity-0'}` : ''}`}>
              <BackButton onClick={
                state.step === 'manual-input' ? () => setState({ step: 'url-input' })
                : state.step === 'scrape-failed' ? () => setState({ step: 'url-input' })
                : handleExit
              } />
            </div>
          )}

          {/* URL input */}
          {state.step === 'url-input' && (
            <div className="flex-1 flex items-center justify-center px-16">
              <div className="max-w-sm">
                <h2 className="text-2xl font-semibold text-content-strong animate-[blurIn_0.6s_ease-out_forwards]" style={{ opacity: 0, animationDelay: '0s' }}>What's your website?</h2>
                <p className="text-content-strong mt-2 text-sm animate-[blurIn_0.6s_ease-out_forwards]" style={{ opacity: 0, animationDelay: '0.1s' }}>
                  We'll analyse it to understand your business, offerings, and ideal customers.
                </p>
                <form onSubmit={handleStartWithUrl} className="mt-8 space-y-5 animate-[blurIn_0.6s_ease-out_forwards]" style={{ opacity: 0, animationDelay: '0.3s' }}>
                  <Input
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    placeholder="https://yourcompany.com"
                    inlineLabel="URL"
                    type="url"
                    autoFocus
                    required
                    className="w-full text-base h-12"
                  />
                  <div className="flex gap-4">
                    <Button type="submit" variant="primary" className="h-12 flex-1" disabled={!websiteUrl.trim()}>
                      Analyse Website
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      className="h-12 shrink-0"
                      onClick={() => setState({ step: 'manual-input' })}
                    >
                      I don't have a website
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Manual input */}
          {state.step === 'manual-input' && (
            <div className="flex-1 flex items-center justify-center px-16">
              <div className="max-w-sm">
                <h2 className="text-2xl font-semibold">Tell us about your business</h2>
                <p className="text-muted-foreground mt-2 text-sm">
                  Describe what your company does, the products or services you offer, and who your ideal customers are.
                </p>
                <form onSubmit={handleStartWithDescription} className="mt-8 space-y-5">
                  <Textarea
                    value={manualDescription}
                    onChange={(e) => setManualDescription(e.target.value)}
                    placeholder="We're a SaaS company that helps..."
                    className="min-h-[160px]"
                    autoFocus
                    required
                  />
                  <div className="flex gap-4">
                    <Button type="submit" variant="primary" className="h-12" disabled={!manualDescription.trim()}>
                      Analyse & Continue
                    </Button>
                    <Button type="button" variant="secondary" className="h-12" onClick={() => setState({ step: 'url-input' })}>
                      Use Website Instead
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Scrape failed */}
          {state.step === 'scrape-failed' && (
            <div className="flex-1 flex items-center justify-center px-16">
              <div className="max-w-sm">
                <h2 className="text-2xl font-semibold">We couldn't read your website</h2>
                <p className="text-muted-foreground mt-2 text-sm">
                  Your site loaded but we couldn't extract enough content — this happens with some website builders. Just describe your business below and we'll take it from there.
                </p>
                <form onSubmit={handleStartWithDescription} className="mt-8 space-y-5">
                  <Textarea
                    value={manualDescription}
                    onChange={(e) => setManualDescription(e.target.value)}
                    placeholder="We're a SaaS company that helps..."
                    className="min-h-[160px]"
                    autoFocus
                    required
                  />
                  <div className="flex gap-4">
                    <Button type="submit" variant="primary" className="h-12" disabled={!manualDescription.trim()}>
                      Analyse & Continue
                    </Button>
                    <Button type="button" variant="secondary" className="h-12" onClick={() => setState({ step: 'url-input' })}>
                      Try a different URL
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Loading spinner */}
          {isLoading && (
            <div className="flex-1 flex items-center justify-center">
              <div className="h-10 w-10 border-2 border-info-solid border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Chat — fades in after panels */}
          {isPhase && (
            <>
              <div className={`h-20 bg-gradient-to-b from-card via-card/80 to-card/0 absolute top-0 left-0 right-0 z-[5] pointer-events-none transition-opacity duration-500 ${showChat ? 'opacity-100' : 'opacity-0'}`} />

              <div className={`flex-1 overflow-y-auto pt-20 pb-52 transition-[opacity] duration-500 ${showChatMessages ? 'opacity-100' : 'opacity-0'}`}>
              <div className="max-w-4xl mx-auto px-7 space-y-5">
                {allMessages.map((msg, i) => (
                  <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                    <div className={`${
                      msg.role === 'user'
                        ? 'bg-surface-subtle text-foreground rounded-3xl rounded-tr-lg'
                        : 'rounded-3xl rounded-tl-lg'
                    } px-3.5 py-2.5 text-[15px] max-w-[85%]`}>
                      <p className="text-foreground">
                        {msg.role === 'assistant'
                          ? <TypewriterText text={msg.content} speed={8} active={showChatMessages} />
                          : msg.content
                        }
                      </p>
                    </div>
                  </div>
                ))}
                {isDiscussing && (
                  <div className="flex gap-2">
                    <div className="rounded-3xl rounded-tl-lg px-3.5 py-2.5 text-sm">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-muted-foreground/30 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-muted-foreground/30 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-muted-foreground/30 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
              </div>

              <div className={`h-48 bg-gradient-to-b from-card/0 to-card absolute bottom-0 left-0 right-0 z-[4] pointer-events-none transition-opacity duration-500 ${showChat ? 'opacity-100' : 'opacity-0'}`} />

              <div className={`absolute bottom-4 left-0 right-0 z-10 transition-[opacity] duration-500 ${showChatInput ? 'opacity-100' : 'opacity-0'}`}>
              <div className="max-w-4xl mx-auto px-5">
                <form onSubmit={handleChatSubmit} className="relative">
                  <Textarea
                    placeholder="Ask me to change something..."
                    value={chatInput}
                    onChange={(e) => {
                      setChatInput(e.target.value);
                      e.target.style.height = 'auto';
                      const lineCount = (e.target.value.match(/\n/g) || []).length + 1;
                      const baseHeight = 120;
                      const lineHeight = 24;
                      const maxHeight = 300;
                      const newHeight = Math.max(baseHeight, Math.min(maxHeight, baseHeight + (lineCount - 1) * lineHeight));
                      e.target.style.height = newHeight + 'px';
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleChatSubmit(e);
                      }
                    }}
                    className="min-h-[120px] resize-none shadow-xl pr-20 overflow-y-auto"
                    disabled={isDiscussing}
                  />
                  {chatInput.trim() && (
                    <Button
                      type="submit"
                      variant="primary"
                      className="absolute bottom-2 right-2"
                      disabled={isDiscussing}
                    >
                      <ArrowUpIcon className="h-4 w-4" />
                    </Button>
                  )}
                </form>
              </div>
              </div>
            </>
          )}
        </div>

        {/* Right panel — persistent, never unmounts */}
        <div
          className="w-[55%] rounded-card m-3 relative overflow-hidden transition-[background-color] duration-700 ease-in-out self-stretch"
          style={{
            backgroundColor: isPhase && !phaseLoading ? 'var(--surface-always-light)' : 'transparent',
          }}
        >
          {/* Animated bg — always present, shows during loading and between phases */}
          <div className={`absolute inset-0 transition-[opacity] duration-700 ${isPhase && !phaseLoading ? 'opacity-0' : 'opacity-100'}`}>
            <HeroBackground className="absolute inset-0 w-full h-full" speed={0.6} timeOffset={0} />

            {/* Logo — centred on animated bg (hidden for now) */}
            {/* <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none" style={{ marginBottom: activityFeed.length > 0 ? '80px' : '0' }}>
              <div className="[&_svg]:w-28 [&_svg]:h-28 opacity-80">
                <LogoIcon className="ml-[calc(1rem-3px)]" />
              </div>
            </div> */}

            {/* Activity feed — centred on animated bg */}
            <div className="absolute inset-0 z-10 overflow-hidden">
              <div
                className="absolute left-0 right-0"
                style={{
                  top: '50%',
                  transition: 'transform 600ms cubic-bezier(0.16, 1, 0.3, 1)',
                  transform: `translateY(-10px)`,
                }}
              >
                {activityFeed.map((activity, i) => {
                  const isLatest = i === activityFeed.length - 1;
                  const distFromEnd = activityFeed.length - 1 - i;
                  // Cylinder effect: items curve upward and away
                  const yOffset = distFromEnd * -32;
                  const opacity = isLatest ? 0 : Math.max(0.15, 0.8 - distFromEnd * 0.15);
                  return (
                    <div
                      key={`${i}-${activity}`}
                      className={`text-center w-full font-medium ${isLatest ? 'animate-[blurIn_0.4s_ease-out_forwards]' : ''}`}
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: 0,
                        transform: `translateY(${yOffset}px)`,
                        opacity,
                        fontSize: '15px',
                        transition: 'transform 600ms cubic-bezier(0.16, 1, 0.3, 1), opacity 600ms ease-out',
                        color: 'var(--on-accent)',
                      }}
                    >
                      {activity}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Data panels — fade in after transition */}
          {isPhase && (
            <>
              <div className={`h-24 absolute top-0 left-0 right-[8px] z-[9] pointer-events-none transition-[opacity] duration-500 ${showDataPanels ? 'opacity-100' : 'opacity-0'}`} style={{ background: 'linear-gradient(to bottom, var(--surface-always-light), color-mix(in oklab, var(--surface-always-light) 90%, transparent), transparent)' }}></div>
              <div className={`h-28 absolute bottom-0 left-0 right-[8px] z-[8] pointer-events-none transition-[opacity] duration-500 ${showDataPanels ? 'opacity-100' : 'opacity-0'}`} style={{ background: 'linear-gradient(to top, var(--surface-always-light), color-mix(in oklab, var(--surface-always-light) 90%, transparent), transparent)' }}></div>

              <div className="absolute top-0 left-0 right-0 z-[10] px-6 pt-5 pb-3 flex items-center justify-between text-on-light-panel [&_p]:!text-on-light-panel [&_h3]:!text-on-light-panel">
                <h3 className={`text-lg font-semibold transition-[opacity] duration-500 ${showDataPanels ? 'opacity-100' : 'opacity-0'}`}>
                  {currentPhase === 1 && 'Business Analysis'}
                  {currentPhase === 2 && 'Campaign Setup'}
                  {currentPhase === 3 && 'Review & Launch'}
                </h3>
                <div className={`flex gap-2 transition-[opacity] duration-500 ${showDataPanels ? 'opacity-100' : 'opacity-0'}`}>
                  {currentPhase > 1 && (
                    <Button variant="secondary" size="sm" onClick={handleBack} disabled={isConfirming}>
                      Back
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleConfirm}
                    disabled={isConfirming}
                  >
                    {isConfirming
                      ? (currentPhase === 3 ? 'Creating...' : 'Validating...')
                      : (currentPhase === 3 ? 'Create Agent & Campaign' : 'Confirm & Continue')
                    }
                  </Button>
                </div>
              </div>

              {challenges.length > 0 && (
                <div className="absolute top-16 left-6 right-6 z-[10] p-3 border border-danger-border bg-danger-surface rounded-lg">
                  <p className="text-xs font-medium text-danger-foreground mb-1">Please address:</p>
                  <ul className="space-y-0.5 text-xs text-danger-text">
                    {challenges.map((challenge, i) => (
                      <li key={i}>- {challenge}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className={`absolute inset-0 overflow-y-auto px-8 pt-28 pb-20 text-on-light-panel [&_h3]:!text-on-light-panel [&_p]:!text-on-light-panel [&_span]:!text-on-light-panel [&_label]:!text-on-light-panel [&_li]:!text-on-light-panel transition-[opacity] duration-700 ${showDataPanels ? 'opacity-100' : 'opacity-0'}`}>
                {currentPhase === 1 && phase1Proposal && (
                  <Phase1BusinessAnalysis proposal={phase1Proposal} onChange={setPhase1Proposal} />
                )}
                {currentPhase === 2 && phase2Proposal && (
                  <Phase2CampaignSetup proposal={phase2Proposal} onChange={setPhase2Proposal} />
                )}
                {currentPhase === 3 && phase1Proposal && phase2Proposal && (
                  <Phase3Review phase1={phase1Proposal} phase2={phase2Proposal} />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  </>);
}
